import { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import {
  type NWTEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import { useParams } from '@xing-com/crate-core-router';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { Head } from '@xing-com/crate-master-directory-head';
import { deslugify } from '@xing-com/crate-master-directory-helpers';
import {
  useGetPopularSkills,
  useGetTopLocations,
} from '@xing-com/crate-master-directory-hooks';
import { LinkList } from '@xing-com/crate-master-directory-links';
import { DirectoryList } from '@xing-com/crate-master-directory-list';
import { SearchHeader } from '@xing-com/crate-master-directory-search-header';
import { useHost } from '@xing-com/crate-xinglet';

import * as S from './job-role.styled';
import { getEmploymentTypeEntries } from './utils';

type Params = {
  letter: string;
  jobRole: string;
};

export const MasterDirectoryJobRolePage: React.FC = () => {
  const { isPreview } = useHost();
  const externalUrl = isPreview
    ? 'https://preview.xing.com'
    : 'https://www.xing.com';
  const { formatMessage } = useIntl();
  const { letter, jobRole } = useParams<Params>();
  const { track } = useTracking<NWTEvent>();
  const jobRoleDeslugified = deslugify(jobRole);
  const description = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_JOB_ROLE_DESCRIPTION' },
    { jobRole: jobRoleDeslugified }
  );
  const title = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_JOB_ROLE_TITLE' },
    { jobRole: jobRoleDeslugified }
  );
  const path = `${ROUTES.jobsDirectory}/${letter.toLocaleLowerCase()}/${jobRole.toLocaleLowerCase()}`;
  const canonicalUrl = `${externalUrl}${path}`;
  const { collection: skillsCollection } = useGetPopularSkills({
    jobRole,
  });
  const { collection: locationsCollection } = useGetTopLocations({
    jobRole,
  });
  const employmentTypeEntries = getEmploymentTypeEntries({
    jobRole,
    jobRoleDeslugified,
    formatMessage,
  });
  const breadcrumbItems = [
    {
      title: formatMessage({
        id: 'CRATE_MASTER_DIRECTORY_BREADCRUMB_HOME',
      }),
      url: `${ROUTES.jobsDirectory}/a`,
    },
    {
      title: letter.toLocaleUpperCase(),
      url: `${ROUTES.jobsDirectory}/${letter}`,
    },
    { title: jobRoleDeslugified, url: '' },
  ];

  useEffect(() => {
    track({
      application: 'wbm',
      eventSchema: 'basic',
      sentBy: 'directory',
      page: 'directory/job_role',
      event: 'viewed_screen',
      type: 'nwt',
    });
  }, [track]);

  return (
    <>
      <Head
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        robots="noodp"
      />

      <SearchHeader />

      <S.Container>
        <S.SeoBreadcrumb items={breadcrumbItems} />

        <S.Hero size="small" sizeConfined="medium">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Hero>

        <LinkList entries={employmentTypeEntries} />

        <S.Headline size="xlarge">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_LOCATIONS_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Headline>
        <LinkList entries={locationsCollection} />

        <S.Headline size="xlarge">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_SKILLS_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Headline>
        <LinkList entries={skillsCollection} showMore />

        <S.Headline size="xlarge">
          <FormattedMessage id="CRATE_MASTER_DIRECTORY_SEARCH_BY_LETTERS" />
        </S.Headline>
        <DirectoryList letter={letter} route={ROUTES.jobsDirectory} />
      </S.Container>
    </>
  );
};
